import { Component, Vue } from 'vue-property-decorator';
import { Route, NavigationGuardNext } from 'vue-router';
import { NOTIFICATION, PAGE_NAMES, PROJECT_ASSESSMENT_STATUSES, PROJECT_PEER_EVALUATION_STATUSES } from '@/constants';
import { sumByField } from '@/helpers';
import { AdjustItem, ProjectAssessmentDTO, ProjectDTO, UserAdjustIndividualDropsDTO, UserDTO } from '@/models';
import {
  buildUserAdjustIndividualDrops,
  fetchProjectPeerEvaluations,
  saveAdjustReceivedDrops,
  sortArrByNumberField,
  sortArrByUserDTOField
} from '@/services';
import ConfirmationDialog from '@/components/common/confirmation-dialog/ConfirmationDialog.vue';
import InlineLoader from '@/components/common/loader/inline-loader/InlineLoader.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import ProjectEvaluationHeader from '@/components/common/project-evaluation-header/ProjectEvaluationHeader.vue';
import Tooltip from '@/components/common/tooltip/Tooltip.vue';
import UserInformation from '@/components/common/user-information/UserInformation.vue';
import AdjustIndividualDropsForm from './adjust-individual-drops-form/AdjustIndividualDropsForm.vue';

@Component({
  components: {
    AdjustIndividualDropsForm,
    ConfirmationDialog,
    InlineLoader,
    Pagination,
    ProjectEvaluationHeader,
    Tooltip,
    UserInformation
  }
})
export default class AdjustIndividualDrops extends Vue {
  $refs!: {
    popupRef: HTMLFormElement;
  };

  private tableHeaderColumns = [
    {
      text: 'Member',
      value: 'user'
    },
    {
      align: 'end',
      class: 'adjust-individual-drops__table-header grey--text',
      text: 'Contribution Pollens',
      value: 'contributionDrops'
    },
    {
      align: 'end',
      class: 'adjust-individual-drops__table-header grey--text',
      text: 'Giving Pollens',
      value: 'givingDrops'
    },
    {
      align: 'end',
      class: 'adjust-individual-drops__table-header grey--text',
      text: 'Received Pollens',
      value: 'receivedDrops'
    },
    {
      align: 'end',
      class: 'adjust-individual-drops__table-header grey--text',
      text: 'Summary Pollens',
      value: 'summaryDrops'
    },
    {
      align: 'end',
      class: 'adjust-individual-drops__table-header grey--text',
      text: 'Adjusted Received Pollens',
      value: 'adjustedDrops'
    },
    {
      align: 'end',
      class: 'adjust-individual-drops__table-header grey--text',
      text: 'Final Individual Pollens',
      value: 'individualDrops'
    },
    {
      align: 'end',
      sortable: false,
      value: 'actions'
    }
  ];
  private next: NavigationGuardNext;
  private projectInfo = {} as ProjectDTO;
  private loading = false;
  private pathTitle = 'Adjust Individual Pollens';
  private saveLoading = false;
  private isEditing = false;
  private adjustConfirmationDialogShown = false;
  private currentUser = {} as UserDTO;
  private projectAssessment = {} as ProjectAssessmentDTO | undefined;
  private members = [] as UserAdjustIndividualDropsDTO[];
  private adjustItem = this.buildAdjustItem();
  private page = 1;
  private pageCount = 0;
  private itemsPerPage = 10;

  get remainingDrops(): number {
    return this.members
      .filter(member => member.adjustedDrops > 0)
      .reduce((acc, curr) => acc + (curr.adjustedDrops - curr.receivedDrops), 0);
  }

  get totalReceivedDrops(): number {
    return sumByField(this.members, 'receivedDrops');
  }

  customSort(
    items: UserAdjustIndividualDropsDTO[],
    fields: string[],
    isDescArr: boolean[]
  ): UserAdjustIndividualDropsDTO[] {
    const field = fields[0];
    if (field) {
      const isDesc = isDescArr[0];
      switch (field) {
        case 'user': {
          return sortArrByUserDTOField(items, field, isDesc);
        }
        default: {
          return sortArrByNumberField(items, field as keyof UserAdjustIndividualDropsDTO, isDesc);
        }
      }
    }
    return items;
  }

  buildAdjustItem(): AdjustItem {
    return {
      totalReceivedDrops: 0,
      remainingDrops: 0,
      userAdjustIndividualDrops: {
        user: {} as UserDTO
      } as UserAdjustIndividualDropsDTO
    };
  }

  resetAdjustItem(): void {
    this.$nextTick(() => {
      this.adjustItem = this.buildAdjustItem();
    });
  }

  openPopupEdit(userAdjustIndividualDrops: UserAdjustIndividualDropsDTO): void {
    this.adjustItem = {
      totalReceivedDrops: this.totalReceivedDrops,
      remainingDrops: this.remainingDrops,
      userAdjustIndividualDrops
    };
    this.$refs.popupRef.openMenu();
  }

  onPopupSave(userAdjustIndividualDrops: UserAdjustIndividualDropsDTO): void {
    this.members = this.members.map(member =>
      member.user.id === userAdjustIndividualDrops.user.id
        ? this.getUserAdjustIndividualDrops(userAdjustIndividualDrops)
        : member
    );
    this.isEditing = true;
    this.resetAdjustItem();
  }

  getUserAdjustIndividualDrops(userAdjustIndividualDrops: UserAdjustIndividualDropsDTO): UserAdjustIndividualDropsDTO {
    return userAdjustIndividualDrops.adjustedDrops !== 0
      ? {
          ...userAdjustIndividualDrops,
          individualDrops:
            userAdjustIndividualDrops.contributionDrops -
            userAdjustIndividualDrops.givingDrops +
            userAdjustIndividualDrops.adjustedDrops
        }
      : userAdjustIndividualDrops;
  }

  async onSave(): Promise<void> {
    this.saveLoading = true;
    const { data } = await saveAdjustReceivedDrops(this.projectAssessment?.id, false, this.members);
    if (!data) {
      const notificationData = {
        showing: true,
        type: NOTIFICATION.TYPE.error,
        title: NOTIFICATION.TEXT.saveAdjustReceivedDropsFailed,
        content: [],
        icon: NOTIFICATION.ICON.error,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', notificationData);
    } else {
      this.isEditing = false;
      this.navigateToProjectEvaluationPage();
    }
    this.saveLoading = false;
  }

  onCancel(): void {
    this.navigateToProjectEvaluationPage();
  }

  navigateToProjectEvaluationPage(): void {
    const { projectId } = this.$route.params;
    this.$router.push({
      name: PAGE_NAMES.PROJECT_EVALUATION,
      params: {
        id: projectId
      }
    });
  }

  navigateToHomePage(): void {
    this.$router.push({ name: PAGE_NAMES.HOME });
  }

  isPeerEvaluationNotDone(projectAssessment: ProjectAssessmentDTO): boolean {
    return projectAssessment.userEngagements.some(
      ue => ue.peerEvaluationStatus === PROJECT_PEER_EVALUATION_STATUSES.STARTED.label
    );
  }

  async created(): Promise<void> {
    this.loading = true;
    const { assessmentId } = this.$route.params;
    this.currentUser = this.$store.getters['app/currentUser'];
    this.projectInfo = this.$store.getters['project/project'];

    this.projectAssessment = (this.projectInfo.projectAssessments || []).find(
      (pa: ProjectAssessmentDTO) => pa.id === +assessmentId
    ) as ProjectAssessmentDTO;

    const canAccess =
      this.projectAssessment &&
      this.projectAssessment.assessmentStatus === PROJECT_ASSESSMENT_STATUSES.PEER_EVALUATION &&
      !this.isPeerEvaluationNotDone(this.projectAssessment);

    if (!canAccess) {
      this.navigateToHomePage();
      return;
    }

    const userEngagements = this.projectAssessment?.userEngagements || [];
    const { data } = await fetchProjectPeerEvaluations(+assessmentId);
    const peerEvaluations = data || [];
    this.members = buildUserAdjustIndividualDrops(userEngagements, peerEvaluations);
    this.$nextTick(() => {
      this.isEditing = false;
      this.loading = false;
    });
  }

  onDialogConfirm(): void {
    this.next();
  }

  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    if (this.isEditing) {
      this.adjustConfirmationDialogShown = true;
      this.next = next;
    } else {
      next();
    }
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }
}
